/**
 * ScenarioParser.ts - WorldSeed Parser and Validator
 * Transforms TatianaAI responses into dynamic world configurations
 */

import {
  WorldSeed,
  WorldSeedToolCall,
  ScenarioResult,
  ParsedIntent,
  EnvironmentType,
  TimeOfDay,
  AtmosphericCondition,
  EnemyDensity,
  TacticalFocus,
  ENVIRONMENT_COLORS,
  TIME_OF_DAY_LIGHTING
} from '../types/world'

// Validation error types
export interface ValidationError {
  field: string
  message: string
  received?: unknown
}

/**
 * Validate WorldSeed structure
 * Returns { valid: true } or { valid: false, errors: [...] }
 */
export function validateWorldSeed(seed: unknown): { valid: true } | { valid: false; errors: ValidationError[] } {
  const errors: ValidationError[] = []
  
  if (!seed || typeof seed !== 'object') {
    return { valid: false, errors: [{ field: 'root', message: 'WorldSeed must be an object' }] }
  }
  
  const s = seed as Record<string, unknown>
  
  // Required fields
  const requiredFields = ['environment', 'time_of_day', 'difficulty', 'objective', 'scriptural_theme']
  for (const field of requiredFields) {
    if (!(field in s)) {
      errors.push({ field, message: `Missing required field: ${field}` })
    }
  }
  
  // Validate environment
  const validEnvironments: EnvironmentType[] = ['urban', 'desert', 'void', 'forest', 'arctic', 'inferno']
  if ('environment' in s && !validEnvironments.includes(s.environment as EnvironmentType)) {
    errors.push({
      field: 'environment',
      message: `Invalid environment. Must be one of: ${validEnvironments.join(', ')}`,
      received: s.environment
    })
  }
  
  // Validate time_of_day
  const validTimes: TimeOfDay[] = ['day', 'night', 'eclipse', 'dawn', 'dusk']
  if ('time_of_day' in s && !validTimes.includes(s.time_of_day as TimeOfDay)) {
    errors.push({
      field: 'time_of_day',
      message: `Invalid time_of_day. Must be one of: ${validTimes.join(', ')}`,
      received: s.time_of_day
    })
  }
  
  // Validate difficulty
  if ('difficulty' in s) {
    const diff = s.difficulty as number
    if (typeof diff !== 'number' || diff < 0.1 || diff > 1.0) {
      errors.push({
        field: 'difficulty',
        message: 'Difficulty must be a number between 0.1 and 1.0',
        received: diff
      })
    }
  }
  
  // Validate objective
  if ('objective' in s && (typeof s.objective !== 'string' || s.objective.length < 5)) {
    errors.push({
      field: 'objective',
      message: 'Objective must be a string with at least 5 characters',
      received: s.objective
    })
  }
  
  // Validate scriptural_theme
  if ('scriptural_theme' in s && (typeof s.scriptural_theme !== 'string' || s.scriptural_theme.length < 3)) {
    errors.push({
      field: 'scriptural_theme',
      message: 'Scriptural theme must be a string with at least 3 characters',
      received: s.scriptural_theme
    })
  }
  
  // Validate optional atmospheric_conditions
  const validAtmospheric: AtmosphericCondition[] = ['clear', 'foggy', 'stormy', 'toxic']
  if ('atmospheric_conditions' in s && s.atmospheric_conditions !== undefined) {
    if (!validAtmospheric.includes(s.atmospheric_conditions as AtmosphericCondition)) {
      errors.push({
        field: 'atmospheric_conditions',
        message: `Invalid atmospheric_conditions. Must be one of: ${validAtmospheric.join(', ')}`,
        received: s.atmospheric_conditions
      })
    }
  }
  
  // Validate optional enemy_density
  const validDensity: EnemyDensity[] = ['sparse', 'moderate', 'dense', 'overwhelming']
  if ('enemy_density' in s && s.enemy_density !== undefined) {
    if (!validDensity.includes(s.enemy_density as EnemyDensity)) {
      errors.push({
        field: 'enemy_density',
        message: `Invalid enemy_density. Must be one of: ${validDensity.join(', ')}`,
        received: s.enemy_density
      })
    }
  }
  
  // Validate optional tactical_focus
  const validTactical: TacticalFocus[] = ['stealth', 'assault', 'defense', 'recon', 'endurance']
  if ('tactical_focus' in s && s.tactical_focus !== undefined) {
    if (!validTactical.includes(s.tactical_focus as TacticalFocus)) {
      errors.push({
        field: 'tactical_focus',
        message: `Invalid tactical_focus. Must be one of: ${validTactical.join(', ')}`,
        received: s.tactical_focus
      })
    }
  }
  
  return errors.length === 0 ? { valid: true } : { valid: false, errors }
}

/**
 * Parse WorldSeed from JSON string or object
 */
export function parseWorldSeed(data: string | object): { success: true; seed: WorldSeed } | { success: false; errors: ValidationError[] } {
  try {
    const parsed = typeof data === 'string' ? JSON.parse(data) : data
    const validation = validateWorldSeed(parsed)
    
    if (!validation.valid) {
      return { success: false, errors: validation.errors }
    }
    
    return { success: true, seed: parsed as WorldSeed }
  } catch (error) {
    return {
      success: false,
      errors: [{ field: 'parse', message: `Failed to parse WorldSeed: ${error}` }]
    }
  }
}

/**
 * Extract WorldSeed from TatianaAI response
 * Looks for JSON blocks containing world_seed
 */
export function extractWorldSeedFromResponse(response: string): { found: true; seed: WorldSeed; toolCall: WorldSeedToolCall } | { found: false } {
  // Match JSON with world_seed field
  const worldSeedRegex = /\{[\s\S]*"world_seed"[\s\S]*\}/g
  const matches = response.match(worldSeedRegex)
  
  if (!matches) return { found: false }
  
  for (const match of matches) {
    try {
      const parsed = JSON.parse(match)
      
      // Check if this is a spawn_portal action with world_seed
      if (parsed.action === 'spawn_portal' && parsed.world_seed) {
        const validation = validateWorldSeed(parsed.world_seed)
        
        if (validation.valid) {
          return {
            found: true,
            seed: parsed.world_seed as WorldSeed,
            toolCall: {
              action: 'spawn_portal',
              game: parsed.game || 'time-fracture',
              world_seed: parsed.world_seed as WorldSeed
            }
          }
        }
      }
    } catch {
      // Continue to next match
    }
  }
  
  return { found: false }
}

/**
 * Parse user intent to generate WorldSeed parameters
 * Used when user describes a scenario in natural language
 */
export function parseUserIntent(message: string): ParsedIntent {
  const lowerMsg = message.toLowerCase()
  const intent: ParsedIntent = { confidence: 0 }
  
  // Environment detection
  const environmentPatterns: Record<EnvironmentType, string[]> = {
    urban: ['urban', 'city', 'ruins', 'buildings', 'streets', 'metropolis', 'industrial'],
    desert: ['desert', 'sand', 'dunes', 'wasteland', 'arid', 'dry'],
    void: ['void', 'space', 'abyss', 'darkness', 'nothingness', 'cosmic'],
    forest: ['forest', 'woods', 'trees', 'jungle', 'nature', 'wilderness'],
    arctic: ['arctic', 'ice', 'snow', 'frozen', 'cold', 'tundra', 'winter'],
    inferno: ['inferno', 'fire', 'lava', 'hell', 'burning', 'flames', 'volcanic']
  }
  
  for (const [env, patterns] of Object.entries(environmentPatterns)) {
    if (patterns.some(p => lowerMsg.includes(p))) {
      intent.environment = env as EnvironmentType
      intent.confidence += 0.2
      break
    }
  }
  
  // Time of day detection
  const timePatterns: Record<TimeOfDay, string[]> = {
    day: ['day', 'daylight', 'noon', 'morning', 'sunlight'],
    night: ['night', 'dark', 'midnight', 'moonlight', 'darkness'],
    eclipse: ['eclipse', 'shadow', 'dark sun', 'blackout'],
    dawn: ['dawn', 'sunrise', 'morning', 'early'],
    dusk: ['dusk', 'sunset', 'evening', 'twilight']
  }
  
  for (const [time, patterns] of Object.entries(timePatterns)) {
    if (patterns.some(p => lowerMsg.includes(p))) {
      intent.time_of_day = time as TimeOfDay
      intent.confidence += 0.2
      break
    }
  }
  
  // Difficulty detection
  const difficultyPatterns: Array<{ pattern: string; value: number }> = [
    { pattern: 'easy', value: 0.3 },
    { pattern: 'simple', value: 0.3 },
    { pattern: 'beginner', value: 0.2 },
    { pattern: 'medium', value: 0.5 },
    { pattern: 'moderate', value: 0.5 },
    { pattern: 'hard', value: 0.8 },
    { pattern: 'difficult', value: 0.8 },
    { pattern: 'extreme', value: 0.95 },
    { pattern: 'impossible', value: 1.0 },
    { pattern: 'nightmare', value: 1.0 }
  ]
  
  for (const { pattern, value } of difficultyPatterns) {
    if (lowerMsg.includes(pattern)) {
      intent.difficulty = value
      intent.confidence += 0.15
      break
    }
  }
  
  // Tactical focus detection
  const tacticalPatterns: Record<TacticalFocus, string[]> = {
    stealth: ['stealth', 'sneak', 'hidden', 'shadow', 'covert', 'silent'],
    assault: ['assault', 'attack', 'combat', 'fight', 'offensive', 'aggressive'],
    defense: ['defense', 'defend', 'protect', 'hold', 'fortify', 'guard'],
    recon: ['recon', 'scout', 'explore', 'discover', 'investigate', 'survey'],
    endurance: ['endurance', 'survive', 'last', 'persist', 'patience', 'survival']
  }
  
  for (const [tactical, patterns] of Object.entries(tacticalPatterns)) {
    if (patterns.some(p => lowerMsg.includes(p))) {
      intent.tactical_focus = tactical as TacticalFocus
      intent.confidence += 0.15
      break
    }
  }
  
  // Enemy density detection
  const densityPatterns: Record<EnemyDensity, string[]> = {
    sparse: ['sparse', 'few', 'minimal', 'scattered', 'thin'],
    moderate: ['moderate', 'some', 'normal', 'average'],
    dense: ['dense', 'many', 'heavy', 'packed', 'crowded'],
    overwhelming: ['overwhelming', 'swarm', 'horde', 'endless', 'massive']
  }
  
  for (const [density, patterns] of Object.entries(densityPatterns)) {
    if (patterns.some(p => lowerMsg.includes(p))) {
      intent.enemy_density = density as EnemyDensity
      intent.confidence += 0.1
      break
    }
  }
  
  return intent
}

/**
 * Generate a default WorldSeed based on partial intent
 */
export function generateDefaultWorldSeed(intent: Partial<ParsedIntent> = {}): WorldSeed {
  const defaults: WorldSeed = {
    environment: 'void',
    time_of_day: 'night',
    difficulty: 0.5,
    objective: 'Navigate the spatial anomaly and reach the extraction point',
    scriptural_theme: 'Psalm 23:4 - Though I walk through the valley of the shadow of death',
    atmospheric_conditions: 'clear',
    enemy_density: 'moderate',
    tactical_focus: 'recon'
  }
  
  return {
    ...defaults,
    ...intent
  } as WorldSeed
}

/**
 * Get portal color based on environment
 */
export function getEnvironmentColor(environment: EnvironmentType): string {
  return ENVIRONMENT_COLORS[environment] || ENVIRONMENT_COLORS.void
}

/**
 * Get lighting configuration based on time of day
 */
export function getTimeOfDayLighting(timeOfDay: TimeOfDay) {
  return TIME_OF_DAY_LIGHTING[timeOfDay] || TIME_OF_DAY_LIGHTING.night
}

/**
 * Format WorldSeed for terminal display
 */
export function formatWorldSeedForTerminal(seed: WorldSeed): string {
  const lines = [
    `[ WORLD SEED GENERATED ]`,
    `  Environment: ${seed.environment.toUpperCase()}`,
    `  Time: ${seed.time_of_day.toUpperCase()}`,
    `  Difficulty: ${(seed.difficulty * 100).toFixed(0)}%`,
    `  Objective: ${seed.objective}`,
    `  Theme: ${seed.scriptural_theme}`
  ]
  
  if (seed.atmospheric_conditions) {
    lines.push(`  Atmosphere: ${seed.atmospheric_conditions.toUpperCase()}`)
  }
  if (seed.enemy_density) {
    lines.push(`  Enemy Density: ${seed.enemy_density.toUpperCase()}`)
  }
  if (seed.tactical_focus) {
    lines.push(`  Tactical Focus: ${seed.tactical_focus.toUpperCase()}`)
  }
  
  return lines.join('\n')
}

/**
 * Strip WorldSeed JSON from display text
 */
export function stripWorldSeedFromText(text: string): string {
  // Remove JSON blocks with world_seed
  const worldSeedRegex = /\{[\s\S]*"world_seed"[\s\S]*\}/g
  return text.replace(worldSeedRegex, '').trim()
}

/**
 * Main scenario generation function
 * Orchestrates the entire world generation pipeline
 */
export async function generateScenario(
  userMessage: string,
  onProgress?: (stage: string, progress: number) => void
): Promise<ScenarioResult> {
  const startTime = Date.now()
  
  try {
    onProgress?.('parsing_intent', 0.1)
    
    // Parse user intent
    const intent = parseUserIntent(userMessage)
    
    onProgress?.('generating_seed', 0.3)
    
    // Generate default seed with parsed intent
    const worldSeed = generateDefaultWorldSeed(intent)
    
    onProgress?.('validating', 0.6)
    
    // Validate the generated seed
    const validation = validateWorldSeed(worldSeed)
    if (!validation.valid) {
      return {
        success: false,
        error: `Validation failed: ${validation.errors.map(e => e.message).join(', ')}`,
        generationTime: Date.now() - startTime
      }
    }
    
    onProgress?.('finalizing', 0.9)
    
    return {
      success: true,
      worldSeed,
      generationTime: Date.now() - startTime
    }
  } catch (error) {
    return {
      success: false,
      error: `Generation failed: ${error}`,
      generationTime: Date.now() - startTime
    }
  }
}

export default {
  validateWorldSeed,
  parseWorldSeed,
  extractWorldSeedFromResponse,
  parseUserIntent,
  generateDefaultWorldSeed,
  getEnvironmentColor,
  getTimeOfDayLighting,
  formatWorldSeedForTerminal,
  stripWorldSeedFromText,
  generateScenario
}
